/**
 * Legalito Connection Card — per-solicitor credential management.
 *
 * Lives on the Profile Settings page. Solicitors paste their personal
 * Legalito email + password; we verify the login at save-time and
 * surface success / failure inline. Password is never displayed back —
 * once saved, the only operations available are "Test connection" and
 * "Disconnect".
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  PlugsConnected, Trash, Pulse, FloppyDisk, CheckCircle, WarningCircle,
} from "@phosphor-icons/react";

export default function LegalitoConnectionCard() {
  const [status, setStatus] = useState(null); // { configured, email, saved_at, token_expires_at }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [testResult, setTestResult] = useState(null); // { ok, error, email, retried_lowercased, tested_at }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/legalito/profile/status");
      setStatus(data);
      if (data.configured) setEmail(data.email || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load Legalito status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!email.trim() || !password) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      await API.post("/legalito/profile/credentials", { email: email.trim(), password });
      toast.success("Legalito connected — credentials verified");
      setPassword("");
      setTestResult(null);
      load();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Failed to save";
      // Surface the exact reason (incl. Legalito's own message) in the
      // persistent panel — a toast is too short for the guidance and is
      // easily missed. The email stays in the field so it can be corrected.
      setTestResult({ ok: false, error: msg, tested_at: new Date().toISOString() });
      toast.error("Legalito did not accept these credentials — see details below");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await API.post("/legalito/profile/test");
      setTestResult(data);
      if (data.ok) toast.success("Legalito login successful");
      else toast.error("Legalito test failed — see details below");
      load();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Test failed";
      setTestResult({ ok: false, error: msg, tested_at: new Date().toISOString() });
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect Legalito? You'll need to re-enter your credentials next time.")) return;
    setBusy(true);
    try {
      await API.delete("/legalito/profile/credentials");
      toast.success("Disconnected");
      setEmail("");
      setPassword("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="h-3 w-32 bg-slate-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="legalito-card">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center">
            <PlugsConnected weight="duotone" className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Legalito — Pre-Contract Enquiries
            </h3>
            <p className="text-xs text-slate-500">
              Connect your personal Legalito account to push cases and sync enquiries.
            </p>
          </div>
        </div>
        {status?.configured && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="legalito-status">
            <CheckCircle weight="fill" className="w-3 h-3" /> Connected
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-3">
        {status?.configured ? (
          <>
            <div className="text-xs text-slate-600 space-y-0.5">
              <div><span className="text-slate-400">Email: </span><span className="font-medium text-slate-800">{status.email}</span></div>
              {status.saved_at && (
                <div><span className="text-slate-400">Saved: </span>{new Date(status.saved_at).toLocaleString("en-GB")}</div>
              )}
              {status.token_expires_at && (
                <div><span className="text-slate-400">Token expires: </span>{new Date(status.token_expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={busy} data-testid="legalito-test">
                <Pulse weight="bold" className="w-3.5 h-3.5 mr-1" /> Test connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={busy} data-testid="legalito-disconnect" className="text-rose-700 hover:bg-rose-50">
                <Trash weight="bold" className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
              <details className="ml-auto">
                <summary className="text-[11px] text-slate-500 underline cursor-pointer">Re-enter password</summary>
                <div className="mt-2 flex flex-wrap gap-2 items-end">
                  <Input
                    type="password"
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-9 text-sm max-w-[240px]"
                    data-testid="legalito-password-update"
                  />
                  <Button size="sm" onClick={handleSave} disabled={busy || !password} data-testid="legalito-save-update">
                    <FloppyDisk weight="bold" className="w-3.5 h-3.5 mr-1" />
                    Update
                  </Button>
                </div>
              </details>
            </div>

            {testResult && (
              <div
                className={`rounded-md border px-3 py-2.5 text-xs ${
                  testResult.ok
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-rose-200 bg-rose-50 text-rose-800"
                }`}
                data-testid="legalito-test-result"
              >
                <div className="flex items-start gap-2">
                  {testResult.ok ? (
                    <CheckCircle weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" />
                  ) : (
                    <WarningCircle weight="fill" className="w-4 h-4 mt-0.5 shrink-0 text-rose-600" />
                  )}
                  <div className="space-y-1">
                    {testResult.ok ? (
                      <>
                        <div className="font-semibold">Connection successful</div>
                        <div>
                          Logged in to Legalito as <span className="font-medium">{testResult.email}</span>.
                          {testResult.retried_lowercased && (
                            <> We had to use the lower-case form of your email — consider re-saving it in lower case.</>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-semibold">Connection failed</div>
                        <div>{testResult.error}</div>
                      </>
                    )}
                    {testResult.tested_at && (
                      <div className="text-[10px] opacity-70">
                        Tested {new Date(testResult.tested_at).toLocaleString("en-GB")}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Legalito email</Label>
                <Input
                  type="email"
                  placeholder="you@yourfirm.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 text-sm mt-1"
                  data-testid="legalito-email-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Legalito password</Label>
                <Input
                  type="password"
                  placeholder="(stored encrypted)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-9 text-sm mt-1"
                  data-testid="legalito-password-input"
                />
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              We log in to Legalito on your behalf, cache the resulting bearer token, and refresh it automatically when it expires.
              Logging in here will invalidate any active browser session on the Legalito website.
            </p>
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                disabled={busy || !email.trim() || !password}
                className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                data-testid="legalito-save"
              >
                <FloppyDisk weight="bold" className="w-4 h-4 mr-1" />
                {busy ? "Verifying…" : "Connect Legalito"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
